<template>
  <div class="header">
    <div class="header__filters">
      <div class="header__item">
        <div class="material-search">
          <CustomInput
            label="Материал"
            placeholder="Введите..."
            :value="this.materialSearchDraft"
            @change_input="onMaterialSearchDraftChange"
            @enter="applyMaterialSearch"
            @clear_input="clearMaterialSearch"
          />
          <CustomButton class="material-search__btn" @click="applyMaterialSearch">
            <span>Поиск</span>
          </CustomButton>
        </div>
      </div>
      <div class="header__item">
        <CustomSelect :options="this.materialTypesOptions" label="Тип материала" option-label-key="material_type_name"
          option-value-key="material_type_id" @change="value => this.onFiltersChange('material_type_id', value)"
          :value="this.currentFilters.material_type_id" placeholder="Не выбрано" />
      </div>
      <div class="header__item">
        <CustomSelect :options="this.providersOptions" label="Поставщик" option-label-key="provider_name"
          option-value-key="provider_id" @change="value => this.onFiltersChange('provider_id', value)"
          :value="this.currentFilters.provider_id" placeholder="Не выбрано" />
      </div>
      <div class="header__item">
        <CustomSelect :options="this.requiredTypesOptions" label="Тип потребности" option-label-key="required_type_name"
          option-value-key="required_type_id" @change="value => this.onFiltersChange('required_type_id', value)"
          :value="this.currentFilters.required_type_id" placeholder="Не выбрано" />
      </div>
      <div class="header__item header__item--checkbox">
        <CustomCheckbox
          :value="this.currentFilters.only_with_prices"
          label="Только с ценами"
          @change="value => this.onFiltersChange('only_with_prices', value)"
        />
      </div>
    </div>
    <div id="create" class="header__actions">
      <span class="records-count">Записей: {{ totalRecords }}</span>
      <CustomButton class="header__create-btn" @click="this.onAddMaterialClick">
        <span>Добавить</span>
        <q-icon name="add" class="header__create-btn-icon" size="16px" />
      </CustomButton>
    </div>
  </div>

  <CustomTable title="Материалы" :rowsData="this.rowsData" :columns="materialsCommonColumns"
    :loading="isFetching" :custom-body="true" :small="true" :showPagination="true"
    :pagesNumber="pagesNumber" :count="totalRecords" :onPaginationChange="handlePaginationChange" :showSearch="true">
    <template #customBody="props" v-if="!isFetching">
      <q-tr>
        <custom-table-cell align="center" vertical-align="center" style="width: 56px">
          <span class="material-id">{{ props.row.material_id || props.row.id }}</span>
        </custom-table-cell>

        <custom-table-cell align="left" vertical-align="center" style="width: 274px">
          <span class="material-name">{{ props.row.material_name || props.row.name }}</span>
        </custom-table-cell>

        <custom-table-cell align="left" vertical-align="center" style="width: 265px">
          <div>
            <span class="material-type">{{ props.row.material_type_name || props.row.type }}</span>
            <div v-if="props.row.required_type_id" class="required-type-sub">
              Тип потребности: {{ props.row.required_type_name }}
            </div>
          </div>
        </custom-table-cell>

        <custom-table-cell align="left" vertical-align="center" style="width: 265px">
          <div>
            <span class="nomenklatura-name">{{ props.row.ssylka__view || '-' }}</span>
            <div v-if="props.row.guid" class="guid-sub">{{ props.row.guid }}</div>
          </div>
        </custom-table-cell>

        <custom-table-cell align="left" vertical-align="center" style="width: 220px">
          <div class="provider-cell">
            <div class="provider-main">
              <span class="provider-name">{{ props.row.provider_name || '-' }}</span>
              <q-tooltip v-if="props.row.provider_name">{{ props.row.provider_name }}</q-tooltip>
            </div>
            <div class="provider-actions">
              <Clickable
                v-if="props.row.has_provider === 0"
                class="provider-link"
                @click.stop="handleAddProvider(props.row)"
              >
                Добавить
              </Clickable>

              <CustomPopup v-else width="1000" :noPadding="true">
                <template #head>
                  <q-icon name="info_outline" color="primary" class="provider-info-icon" />
                </template>
                <template #default>
                  <LinksTable :material_id="props.row.material_id" :filters="this.currentFilters"
                    @refresh-materials-data="refreshMaterialsData" />
                </template>
              </CustomPopup>
            </div>
          </div>
        </custom-table-cell>

        <custom-table-cell align="right" vertical-align="center" style="width: 160px">
          <div class="price-cell">
            <div class="price-main">
              <CustomPopup
                v-if="props.row.provider_id && (props.row.material_id || props.row.id)"
                width="1000"
                :noPadding="true"
              >
                <template #head>
                  <SvgIcon
                    name="clock"
                    color="#EA690C"
                    class="price-main__clock"
                  />
                </template>
                <template #default>
                  <PriceTable
                    :provider="getPriceHistoryProvider(props.row)"
                    :filters="this.currentFilters"
                    @refresh-materials-data="refreshMaterialsData"
                  />
                </template>
              </CustomPopup>
              <SvgIcon
                v-else
                name="clock"
                color="#EA690C"
                class="price-main__clock price-main__clock--disabled"
              />
              <template v-if="props.row.price_whole !== null && props.row.price_whole !== undefined">
                {{ `${numberWithCommas(props.row.price_whole)} ₸` }}
              </template>
              <template v-else>-</template>
            </div>
            <div
              v-if="props.row.price_whole_nds !== null && props.row.price_whole_nds !== undefined"
              class="price-nds"
            >
              с НДС: {{ `${numberWithCommas(props.row.price_whole_nds)} ₸` }}
            </div>
          </div>
        </custom-table-cell>

        <custom-table-cell align="center" vertical-align="center" style="width: 120px">
          <span class="unit-cell">
            {{ props.row.consume_unit || '-' }}
          </span>
        </custom-table-cell>

        <custom-table-cell align="center" vertical-align="center">
          <div class="row-actions">
            <SvgIcon
              name="edit"
              color="#1BB81B"
              class="action-icon"
              @click.stop="onEditMaterialClick(props.row)"
            />
            <CustomIcon
              class="action-icon"
              icon="trash"
              approveMode
              :approve="() => this.delete(props.row?.material_id)"
            />
          </div>
        </custom-table-cell>
      </q-tr>
    </template>
  </CustomTable>
</template>

<script>
import { useCustomSnackbar } from '@/_helpers/hooks/useCustomSnackbar'
import Clickable from '@/components/common/Clickable'
import CustomButton from '@/components/common/CustomButton'
import CustomIcon from '@/components/common/CustomIcon'
import CustomPopup from '@/components/common/CustomPopup'
import CustomTable from '@/components/common/CustomTable'
import CustomTableCell from '@/components/common/CustomTable/custom-table-cell'
import PageTitle from '@/components/common/PageTitle'
import SvgIcon from '@/components/common/SvgIcon'
import CustomInput from '@/components/form/CustomInput'
import CustomSelect from '@/components/form/CustomSelect'
import CustomCheckbox from '@/components/form/CustomCheckbox'
import { MODAL_NAMES } from '@/constants'
import { useAppStore } from '@/services/store/app'
import {
  getMaterials,
  getMaterialTypesFilter,
  getProvidersFilter,
  getRequiredTypesFilter,
  materialsCommonColumns,
  updMaterial,
  updProvider,
  deleteMaterial
} from '@/views/Materials/services'
import LinksTable from './LinksTable.vue'
import PriceTable from './PriceTable.vue'
import {numberWithCommas} from "@/services"

export default {
  name: 'Materials',
  components: {
    CustomTableCell,
    LinksTable,
    PriceTable,
    CustomPopup,
    CustomTable,
    CustomIcon,
    CustomSelect,
    CustomInput,
    CustomButton,
    PageTitle,
    SvgIcon,
    Clickable,
    CustomCheckbox,
  },
  data() {
    return {
      materialsCommonColumns,
      rowsData: [],
      loading: false,
      isFetching: false,
      whichTab: 'zakup',
      tabLabel: 'Отдел закупа',
      currentFilters: {
        material_type_id: this.$route.query?.material_type_id
          ? Number(this.$route.query?.material_type_id)
          : '',
        provider_id: this.$route.query?.provider_id
          ? Number(this.$route.query?.provider_id)
          : '',
        required_type_id: this.$route.query?.required_type_id
          ? Number(this.$route.query?.required_type_id)
          : '',
        material_search: this.$route.query?.material_search
          ? this.$route.query?.material_search
          : '',
        only_with_prices: this.$route.query?.only_with_prices === 'true',
      },
      materialSearchDraft: this.$route.query?.material_search
        ? this.$route.query?.material_search
        : '',
      providersOptions: [],
      materialTypesOptions: [],
      requiredTypesOptions: [],
      isFetchingFilters: false,
      isMaterialEdit: false,
      totalRecords: 0,
      pagination: {
        page: 1,
        limit: 1000,
      },
    }
  },
  computed: {
    pagesNumber() {
      return Math.ceil(this.totalRecords / this.pagination.limit);
    },
  },
  async mounted() {
    this.getData()
    this.getSelectsOptions()
  },
  methods: {
    numberWithCommas,
    getPriceHistoryProvider(row) {
      return {
        material_id: row.material_id || row.id,
        provider_id: row.provider_id,
        material_provider_id: row.material_provider_id,
      }
    },
    async getData() {
      this.loading = true
      this.isFetching = true
      const queryParams = {
        ...this.currentFilters,
        page: this.pagination.page,
        limit: this.pagination.limit,
      }
      if (!this.currentFilters.only_with_prices) delete queryParams.only_with_prices
      const res = await getMaterials(queryParams)
      this.loading = false
      this.isFetching = false
      if (!res) return
      this.rowsData = res?.data || []
      this.totalRecords = res?.total_count || 0
    },
    async updateDataSilently() {
      // Обновляем данные без показа индикатора загрузки
      const queryParams = {
        ...this.currentFilters,
        page: this.pagination.page,
        limit: this.pagination.limit,
      }
      if (!this.currentFilters.only_with_prices) delete queryParams.only_with_prices
      const res = await getMaterials(queryParams)
      if (!res) return
      this.rowsData = res?.data || []
      this.totalRecords = res?.total_count || 0
    },
    handlePaginationChange(newPage) {
      this.pagination.page = newPage;
      this.getData();
    },
    async getSelectsOptions() {
      const materialTypeParams = {}
      if (this.currentFilters.material_search) {
        materialTypeParams.material_search = this.currentFilters.material_search
      }

      const providerParams = {}
      if (this.currentFilters.material_search) {
        providerParams.material_search = this.currentFilters.material_search
      }
      if (this.currentFilters.material_type_id) {
        providerParams.material_type_id = this.currentFilters.material_type_id
      }

      const providersOptions = await getProvidersFilter(providerParams)
      const materialTypesOptions = await getMaterialTypesFilter(materialTypeParams)
      const requiredTypesOptions = await getRequiredTypesFilter()

      this.providersOptions = !providersOptions ? [] : providersOptions
      this.materialTypesOptions = !materialTypesOptions
        ? []
        : materialTypesOptions
      this.requiredTypesOptions = !requiredTypesOptions ? [] : requiredTypesOptions
    },
    onMaterialSearchDraftChange(value) {
      this.materialSearchDraft = value
    },
    clearMaterialSearch() {
      this.materialSearchDraft = ''
      this.applyMaterialSearch()
    },
    applyMaterialSearch() {
      const value = this.materialSearchDraft || ''
      if (this.currentFilters.material_search === value) return

      this.currentFilters.material_search = value
      this.$router.push({
        query: { ...this.$route.query, material_search: value || undefined },
      })
      this.pagination.page = 1
      this.getSelectsOptions()
      this.getData()
    },
    onFiltersChange(key, value) {
      if (this.currentFilters[key] === value) return
      this.currentFilters[key] = value
      // Для boolean фильтров
      const queryValue = typeof value === 'boolean' ? (value ? 'true' : undefined) : (value || undefined);
      this.$router.push({
        query: { ...this.$route.query, [key]: queryValue },
      })
      this.pagination.page = 1
      this.getData()
    },
    initializeFiltersFromURL() {
      const query = this.$route.query
      if (query) {
        Object.keys(this.currentFilters).forEach(key => {
          if (query[key]) {
            const numericKeys = ['material_type_id', 'provider_id', 'required_type_id'];
            const booleanKeys = ['only_with_prices'];
            if (numericKeys.includes(key)) {
              this.currentFilters[key] = Number(query[key])
            } else if (booleanKeys.includes(key)) {
              this.currentFilters[key] = query[key] === 'true'
            } else {
              this.currentFilters[key] = query[key]
            }
          }
        })
      }
    },
    onAddMaterialClick() {
      this.showModal(MODAL_NAMES.AddMaterial, {
        title: 'Добавить материал',
        onSubmit: this.handleAddMaterial,
        wide: true,
      })
    },
    onEditMaterialClick(row) {
      this.showModal(MODAL_NAMES.AddMaterial, {
        onSubmit: body => this.handleUpdateMaterial(body),
        title: 'Редактировать материал',
        body: row,
        wide: true,
      })
    },
    async handleAddMaterial(body) {
      const res = await updMaterial(body, this.currentFilters)
      if (!res) {
        return
      }
      // Реактивное обновление без индикатора загрузки
      this.rowsData = res
      this.showSucceedSnackbar()
      this.closeModal()
    },
    async handleUpdateMaterial(body) {
      const res = await updMaterial(body, this.currentFilters)
      if (!res) {
        return
      }
      // Реактивное обновление без индикатора загрузки
      this.rowsData = res
      this.showSucceedSnackbar()
      this.closeModal()
    },
    async handleProviderEntry(body) {
      const params = {
        material_id: body.material_id
      }
      await updProvider(body, params);
      // Реактивное обновление без индикатора загрузки
      await this.getData();
      this.showSucceedSnackbar();
      this.closeModal();
    },
    handleAddProvider(row) {
      this.showModal(MODAL_NAMES.AddProviderEntry, {
        title: 'Добавить поставщика',
        onSubmit: this.handleProviderEntry,
        body: {
          material_id: row.material_id,
        },
      })
    },
    async delete(material_id) {
      const res = await deleteMaterial(material_id, this.currentFilters)
      if (!res) return
      // Реактивное обновление без индикатора загрузки
      this.rowsData = res
      this.showSucceedSnackbar()
    },
    refreshMaterialsData() {
      this.getData();
    },
  },
  created() {
    this.initializeFiltersFromURL()
    this.getData()
  },
  setup() {
    const { showSucceedSnackbar, showErrorSnackbar } = useCustomSnackbar()
    const { showModal, closeModal } = useAppStore()
    return { showSucceedSnackbar, showErrorSnackbar, showModal, closeModal }
  },
}
</script>

<style scoped lang="scss">
// Стили для строк таблицы
:deep(.q-table tbody tr) {
  transition: background-color 0.15s ease;
  
  &:nth-child(even) {
    background-color: #f8fafc;
  }
  
  &:hover {
    background-color: #e0f2fe !important;
  }
}

// ID материала
.material-id {
  color: #64748b;
  font-weight: 500;
}

// Название материала
.material-name {
  font-weight: 500;
  color: #1e293b;
  font-size: 14px;
}

// Тип материала
.material-type {
  color: #475569;
  font-size: 13px;
}

.required-type-sub {
  color: #4caf50;
  font-size: 11px;
  margin-top: 2px;
}

// Номенклатура 1С
.nomenklatura-name {
  color: #334155;
}

.guid-sub {
  font-family: 'Courier New', monospace;
  font-size: 10px;
  color: #0284c7;
  margin-top: 2px;
  opacity: 0.8;
}

.records-count {
  font-size: 13px;
  color: #64748b;
  font-weight: 500;
  background: #f1f5f9;
  padding: 6px 12px;
  border-radius: 6px;
}

.unit-cell {
  color: #64748b;
  font-size: 13px;
}

.provider-cell {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-width: 0;
}

.provider-main {
  min-width: 0;
}

.provider-name {
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #4338ca;
  font-weight: 500;
}

.provider-actions {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.provider-info-icon {
  font-size: 18px;
  cursor: pointer;
  opacity: 0.7;
  transition: all 0.15s ease;
  
  &:hover {
    opacity: 1;
    transform: scale(1.15);
  }
}

.provider-link {
  font-size: 12px;
  color: #0284c7;
  font-weight: 500;
  
  &:hover {
    color: #0369a1;
    text-decoration: underline;
  }
}

.no-provider {
  color: #94a3b8;
  font-style: italic;
  font-size: 12px;
}

.price-cell {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  line-height: 1.2;
}

.price-main {
  display: flex;
  align-items: center;
  gap: 5px;
  font-weight: 600;
  color: #059669;
  font-size: 14px;
}

.price-main__clock {
  cursor: pointer;
  width: 18px !important;
  height: 18px !important;
  transition: transform 0.15s ease, opacity 0.15s ease, filter 0.15s ease;

  &:hover {
    transform: scale(1.1);
    filter: drop-shadow(0 0 2px rgba(234, 105, 12, 0.5));
  }

  &:active {
    transform: scale(0.95);
  }
}

.price-main__clock--disabled {
  cursor: not-allowed;
  opacity: 0.45;

  &:hover,
  &:active {
    transform: none;
    filter: none;
  }
}

.price-nds {
  font-size: 11px;
  color: #6b7280;
  margin-top: 2px;
}

.price-sub {
  font-size: 10px;
  color: #6b7280;
  margin-top: 2px;
}

.row-actions {
  display: inline-flex;
  align-items: center;
  gap: 12px;
}

.action-icon {
  width: 18px;
  height: 18px;
  cursor: pointer;
  opacity: 0.7;
  transition: all 0.15s ease;
  padding: 4px;
  border-radius: 4px;
  
  &:hover {
    opacity: 1;
    background-color: #f1f5f9;
    transform: scale(1.1);
  }
}

.header {
  display: flex;
  gap: 32px;
  margin-bottom: 24px;
  justify-content: space-between;
  align-items: end;

  &__filters {
    display: flex;
    gap: 32px;
  }

  &__item {
    width: 280px;
  }

  &__actions {
    display: flex;
    gap: 16px;
    align-items: center;
  }

  &__create-btn {
    width: auto;
    height: 46px;
  }

  &__edit-btn {
    width: auto;
    height: 46px;
    background-color: #0060fe;
    color: white;
  }

  &__create-btn-icon {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background-color: #fff;
    color: #0060fe;
  }

  &__edit-btn-icon {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background-color: #fff;
    color: #0060fe;
  }

  &__delete-btn {
    width: auto;
    height: 46px;
    background-color: #ff6c6c;
    color: white;
  }

  &__selected-actions {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  :deep(.q-btn__content) {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  :deep(.customBtn) {
    width: auto;
  }

  @media (max-width: 400px) {
    &__item {
      width: 100%;
    }
  }
}

.material-search {
  display: flex;
  gap: 12px;
  align-items: flex-end;
}

.material-search__btn {
  height: 40px;
  width: auto;
  flex-shrink: 0;
}

.header__item--checkbox {
  width: auto;
  display: flex;
  align-items: flex-end;
  padding-bottom: 4px;
}

.provider-info {
  display: flex;
  justify-content: center;
  align-items: center;
}

.provider-info-item {
  display: flex;
  gap: 8px;
  align-items: center;
}

.provider-tag {
  width: 145px;
  padding: 9px 8px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 12px;
  line-height: 16px;
  color: #373d48;
  white-space: nowrap;
  overflow: hidden;
  cursor: pointer;
}

.provider-price {
  width: 105px;
  padding: 9px 8px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 12px;
  line-height: 16px;
  color: #373d48;
  white-space: nowrap;
}

.provider-icon {
  color: #0060fe;
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    opacity: 0.8;
  }
}

:deep(.q-table) {
  background: #ffffff;
  border-radius: 8px;
  border: 1px solid #d1d5db;
}

:deep(.q-table thead tr th) {
  background: #f2f5f8;
  border-bottom: 1px solid #d1d5db;
  font-size: 12px;
  line-height: 16px;
  color: #373d48;
  font-weight: 400;
  padding: 14px 12px;
}

:deep(.q-table tbody tr) {
  border-bottom: 1px solid #d1d5db;
  background: #ffffff;
}

:deep(.q-table tbody tr:hover) {
  background: #f9fafb;
}

:deep(.q-table tbody td) {
  font-size: 12px;
  line-height: 16px;
  color: #373d48;
  padding: 10px 12px;
}
</style>
